import toast from "react-hot-toast";
import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import {
  queryOptions,
  useMutation,
  usePrefetchQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import type { ExamDto, SaveExamDto } from "~/data/admin/checklist/exam.dto";
import type { SaveChecklistDto } from "~/data/admin/checklist/checklist.dto";
import type {
  QuestionDto,
  SaveQuestionDto,
} from "~/data/admin/checklist/question.dto";

import { Field } from "~/ui/shared/field";
import { Input } from "~/ui/shared/input";
import { Button } from "~/ui/shared/button";
import { Dialog } from "~/ui/shared/dialog";
import { mask } from "~/ui/shared/utils/mask";
import { IconButton } from "~/ui/shared/icon-button";
import { debounce } from "~/ui/shared/utils/debounce";
import { getCategories } from "~/data/category/category.service";
import { HtmlEditor, SimpleHtmlEditor } from "~/ui/shared/html-editor";
import {
  createChecklistExam,
  createChecklistQuestion,
  deleteChecklist,
  deleteChecklistExam,
  deleteChecklistQuestion,
  getChecklist,
  getChecklistExams,
  getChecklistQuestions,
  updateChecklist,
  updateChecklistExam,
  updateChecklistQuestion,
} from "~/data/admin/checklist/checklist.service";

const checklistQuery = (checklistId: string) =>
  queryOptions({
    queryKey: ["admin_checklist", checklistId],
    queryFn: () => getChecklist(checklistId),
  });
const checklistExamsQuery = (checklistId: string) =>
  queryOptions({
    queryKey: ["admin_checklist_exams", checklistId],
    queryFn: () => getChecklistExams(checklistId),
  });
const checklistQuestionsQuery = (checklistId: string) =>
  queryOptions({
    queryKey: ["admin_checklist_questions", checklistId],
    queryFn: () => getChecklistQuestions(checklistId),
    structuralSharing: false,
  });

const categoriesQuery = () =>
  queryOptions({
    queryKey: ["categories"],
    queryFn: () => getCategories(),
  });

export function Checklist() {
  const navigate = useNavigate();
  const params = useParams();
  const checklistId = params.id!;

  usePrefetchQuery(checklistExamsQuery(checklistId));
  usePrefetchQuery(checklistQuestionsQuery(checklistId));
  usePrefetchQuery(categoriesQuery());

  const queryClient = useQueryClient();
  const { data: checklist } = useSuspenseQuery(checklistQuery(checklistId));
  const { data: categories } = useSuspenseQuery(categoriesQuery());
  const { mutate: saveChecklist } = useMutation({
    mutationFn: (dto: SaveChecklistDto) => updateChecklist(checklistId, dto),
    onSuccess: (data) =>
      queryClient.setQueryData(checklistQuery(checklistId).queryKey, data),
  });
  const publishMutation = useMutation({
    mutationFn: () => updateChecklist(checklistId, { status: "active" }),
    onSuccess: (data) =>
      queryClient.setQueryData(checklistQuery(checklistId).queryKey, data),
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteChecklist(checklistId),
  });
  const form = useForm({ defaultValues: checklist });

  // Auto saves form's data
  useEffect(() => {
    const subscription = form.watch(
      debounce(() => form.handleSubmit((data) => saveChecklist(data))(), 500)
    );
    return subscription.unsubscribe;
  }, [form, saveChecklist]);

  return (
    <div className="p-10">
      <header className="mb-5 flex justify-between items-center py-2">
        <div>
          <h2 className="text-3xl font-semibold mb-1">{checklist.title}</h2>
        </div>

        <IconButton
          type="button"
          aria-label="Deletar Checklist"
          icon={<MdDelete size={20} />}
          loading={deleteMutation.isPending}
          onClick={() => {
            const confirmed = confirm(
              "Esse checklist será permanentemente excluido. Confirmar?"
            );
            if (!confirmed) return;
            deleteMutation.mutate(void 0, {
              onSuccess: () => navigate("/checklists"),
            });
          }}
        />
      </header>

      <div className="grid gap-3 mb-6">
        <Field label="Titulo">
          <Input {...form.register("title")} />
        </Field>

        <Field label="Categoria">
          <select {...form.register("test_category_id")}>
            {categories?.map((it) => (
              <option key={it.id} value={it.id}>
                {it.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Cenário de Atuação">
          <Controller
            control={form.control}
            name="acting_scenario"
            render={({ field }) => (
              <HtmlEditor html={field.value} onHtmlChange={field.onChange} />
            )}
          />
        </Field>

        <Field label="Tarefas a realizar">
          <Controller
            control={form.control}
            name="tasks"
            render={({ field }) => (
              <HtmlEditor html={field.value} onHtmlChange={field.onChange} />
            )}
          />
        </Field>

        <Field label="Orientação do ator">
          <Controller
            control={form.control}
            name="actor_orientations"
            render={({ field }) => (
              <HtmlEditor html={field.value} onHtmlChange={field.onChange} />
            )}
          />
        </Field>

        <ChecklistExams checklistId={checklistId} />

        <ChecklistQuestions checklistId={checklistId} />
      </div>

      <Button
        variant="primary-2"
        className="h-14 w-64 ml-auto"
        loading={publishMutation.isPending}
        onClick={() =>
          publishMutation.mutate(void 0, {
            onSuccess: () => toast.success("Publicado!"),
          })
        }
      >
        Publicar
      </Button>
    </div>
  );
}

function ChecklistExams({ checklistId }: { checklistId: string }) {
  const queryClient = useQueryClient();
  const { data: exams } = useSuspenseQuery(checklistExamsQuery(checklistId));
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteChecklistExam(id),
    onSuccess: () =>
      queryClient.invalidateQueries(checklistExamsQuery(checklistId)),
  });

  return (
    <section className="mb-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-semibold font-alternative">Impressos</h3>

        <Dialog>
          <Dialog.Trigger asChild>
            <IconButton
              type="button"
              aria-label="Novo Exame"
              icon={<MdAdd size={20} />}
            />
          </Dialog.Trigger>
          <Dialog.Content className="max-w-xl">
            <ChecklistExamForm checklistId={checklistId} />
          </Dialog.Content>
        </Dialog>
      </div>
      <ul className="grid gap-3">
        {exams.map((exam) => (
          <li
            className="flex rounded-[10px] items-center justify-between shadow-card p-4"
            key={`exam:${exam.id}`}
          >
            <p className="text-sm font-alternative font-semibold">
              {exam.title}
            </p>
            <div className="flex items-center gap-2">
              <Dialog>
                <Dialog.Trigger asChild>
                  <IconButton
                    type="button"
                    aria-label="Editar Exame"
                    icon={<MdEdit size={20} />}
                  />
                </Dialog.Trigger>
                <Dialog.Content className="max-w-xl">
                  <ChecklistExamForm checklistId={checklistId} exam={exam} />
                </Dialog.Content>
              </Dialog>
              <IconButton
                type="button"
                aria-label="Deletar Exame"
                icon={<MdDelete size={20} />}
                loading={deleteMutation.isPending}
                onClick={() => {
                  const confirmed = confirm(
                    "Esse impresso será excluido. Confirmar?"
                  );
                  if (!confirmed) return;
                  deleteMutation.mutate(exam.id);
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ChecklistExamForm({
  checklistId,
  exam,
}: {
  checklistId: string;
  exam?: ExamDto;
}) {
  const dialogCloseRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const saveMutation = useMutation({
    mutationFn: (dto: SaveExamDto) =>
      exam
        ? updateChecklistExam(exam.id, dto)
        : createChecklistExam(checklistId, dto),
    onSuccess: () =>
      queryClient.invalidateQueries(checklistExamsQuery(checklistId)),
  });
  const form = useForm<SaveExamDto>({
    defaultValues: { ...exam, file: undefined },
  });

  const file = form.watch("file");
  const fileUrl =
    file instanceof File
      ? URL.createObjectURL(file)
      : !!file && import.meta.env.VITE_API_URL + file;

  return (
    <form
      onSubmit={form.handleSubmit((data) =>
        saveMutation.mutate(data, {
          onSuccess: () => dialogCloseRef.current?.click(),
        })
      )}
    >
      <Dialog.Close ref={dialogCloseRef} />

      <h3 className="text-xl font-semibold mb-4">
        {exam ? "Editar Impresso" : "Novo Impresso"}
      </h3>

      <Field label="Título" className="mb-3">
        <Input {...form.register("title")} />
      </Field>
      <Field label="Conteúdo" className="mb-3">
        <Controller
          control={form.control}
          name="content"
          render={({ field }) => (
            <HtmlEditor html={field.value} onHtmlChange={field.onChange} />
          )}
        />
      </Field>
      <Field label="Anexo">
        <Controller
          control={form.control}
          name="file"
          render={({ field }) => (
            <Input
              type="file"
              className="py-2"
              onChange={(e) => {
                const fileList = e.target.files;
                const file = fileList?.item(0);
                if (!file || !file.name) return;
                field.onChange(file);
              }}
            />
          )}
        />
      </Field>
      {fileUrl && <img className="mt-3" src={fileUrl} />}

      <Button
        loading={saveMutation.isPending}
        variant="primary-1"
        className="ml-auto mt-5 h-12 w-64"
      >
        Salvar
      </Button>
    </form>
  );
}

function ChecklistQuestions({ checklistId }: { checklistId: string }) {
  const queryClient = useQueryClient();
  const {
    data: { questions, categories },
  } = useSuspenseQuery(checklistQuestionsQuery(checklistId));
  const createQuestionMutation = useMutation({
    mutationFn: () =>
      createChecklistQuestion(checklistId, {
        question: "",
        question_category_id: categories?.[0].id ?? "",
        question_answers_attributes: [{ score: 1 }, { score: 0 }],
      }),
    onSuccess: () =>
      queryClient.invalidateQueries(checklistQuestionsQuery(checklistId)),
  });
  return (
    <section>
      <h3 className="text-xl font-semibold font-alternative mb-3">Questões</h3>
      <table className="bg-gray-100 border-collapse border-slate-200 w-full">
        <thead>
          <tr>
            <th className="border px-2 py-1 text-sm font-alternative font-medium">
              Itens Avaliados
            </th>
            <th className="border px-2 py-1 text-sm font-alternative font-medium">
              Categoria
            </th>
            <th className="border px-2 py-1 text-sm font-alternative font-medium">
              Adequado
            </th>
            <th className="border px-2 py-1 text-sm font-alternative font-medium">
              Parcialmente Adequado
            </th>
            <th className="border px-2 py-1 text-sm font-alternative font-medium">
              Inadequado
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody className="bg-gray-50">
          {questions.map((it) => {
            return (
              <ChecklistQuestionRow
                question={it}
                categories={categories}
                key={it.id}
              />
            );
          })}
          <tr>
            <td colSpan={10}>
              <div className="pt-5 flex items-center justify-between">
                <span className="text-lg">
                  Total:{" "}
                  {questions.reduce(
                    (total, it) => total + (it.answers[0]?.score ?? 0),
                    0
                  )}
                </span>

                <Button
                  type="button"
                  variant="alternative-2"
                  className="h-14 w-64 ml-auto"
                  onClick={() => createQuestionMutation.mutate()}
                  loading={createQuestionMutation.isPending}
                >
                  Adicionar Questão
                </Button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

const getFormValues = (question: QuestionDto): SaveQuestionDto => {
  const attrs: unknown[] = question.answers.map((answer) => ({
    id: answer.id,
    score: answer.score,
  }));
  if (attrs.length < 3) {
    attrs.splice(1, 0, void 0); // fill middle index to allow "partially" field!!!
  }
  return {
    question: question.question,
    question_category_id: question.question_category_id,
    question_answers_attributes:
      attrs as SaveQuestionDto["question_answers_attributes"],
  };
};
function ChecklistQuestionRow({
  question,
  categories,
}: {
  question: QuestionDto;
  categories?: { id: string; name: string }[];
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: saveQuestionAsync } = useMutation({
    mutationFn: (dto: SaveQuestionDto) =>
      updateChecklistQuestion(question.id, dto),
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteChecklistQuestion(question.id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["admin_checklist_questions"],
      }),
  });
  const form = useForm<SaveQuestionDto>({
    defaultValues: getFormValues(question),
  });

  const onSave = form.handleSubmit(async (data) => {
    const formatAnswersAttributes = () => {
      const attributes = data.question_answers_attributes;
      if (attributes.length >= 3 && !attributes[1].score) {
        attributes[1]._destroy = true;
      }
      return attributes;
    };
    const result = await saveQuestionAsync({
      ...data,
      question_answers_attributes: formatAnswersAttributes(),
    });
    form.reset(getFormValues(result));
  });

  const formatter = (value: string) => {
    if (!value) return value;
    return value.replace(/[^0-9.]/g, "").replace(/(\..*?)\./g, "$1");
  };

  return (
    <tr key={question.id}>
      <td className="border max-w-1/2 w-full">
        <Controller
          control={form.control}
          name="question"
          render={({ field }) => (
            <SimpleHtmlEditor
              html={field.value}
              onHtmlChange={field.onChange}
            />
          )}
          rules={{ onChange: onSave }}
        />
      </td>
      <td className="border px-4">
        <select
          {...form.register("question_category_id", { onChange: onSave })}
        >
          {categories?.map((it) => (
            <option key={it.id} value={it.id}>
              {it.name}
            </option>
          ))}
        </select>
      </td>
      <td className="border px-4 w-1/3 max-w-0">
        <input
          className="bg-gray-50 block w-full text-center outline-none"
          onInput={mask(formatter)}
          {...form.register(`question_answers_attributes.0.score`, {
            onBlur: onSave,
          })}
        />
      </td>
      <td className="border px-4 w-1/3 max-w-0">
        <input
          className="bg-gray-50 block w-full text-center outline-none"
          onInput={mask(formatter)}
          {...form.register(`question_answers_attributes.1.score`, {
            onBlur: onSave,
          })}
        />
      </td>
      <td className="border px-4 w-1/3 max-w-0">
        <input
          className="bg-gray-50 block w-full text-center outline-none"
          onInput={mask(formatter)}
          {...form.register(`question_answers_attributes.2.score`, {
            onBlur: onSave,
          })}
        />
      </td>
      <td className="border px-4">
        <IconButton
          type="button"
          aria-label="Deletar Questão"
          icon={<MdDelete size={20} />}
          loading={deleteMutation.isPending}
          onClick={() => {
            const confirmed = confirm("Essa questão será excluida. Confirmar?");
            if (!confirmed) return;
            deleteMutation.mutate();
          }}
        />
      </td>
    </tr>
  );
}
